import React, { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { createMotion } from '../lib/motion';

const MotionContext = createContext({ scan: () => {} });

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Owns one scroll-motion engine for the whole session. The engine is created
 * lazily on the first scan (child effects run before parent effects, so the
 * first page would otherwise scan before the engine exists).
 */
export function MotionProvider({ children }) {
  const engineRef = useRef(null);
  const apiRef = useRef(null);
  if (!apiRef.current) {
    apiRef.current = {
      scan() {
        if (typeof window === 'undefined') return;
        if (!engineRef.current) engineRef.current = createMotion({ intensity: 1, parallax: 1 });
        engineRef.current.scan();
      },
    };
  }
  useEffect(
    () => () => {
      if (engineRef.current) engineRef.current.destroy();
      engineRef.current = null;
    },
    []
  );
  return <MotionContext.Provider value={apiRef.current}>{children}</MotionContext.Provider>;
}

/**
 * Pages call this so every render (including client-side navigation) is
 * scanned for motion attributes before the browser paints.
 */
export function useMotionScan() {
  const { scan } = useContext(MotionContext);
  useIsomorphicLayoutEffect(() => {
    scan();
  });
}
